import { useMemo, useState } from 'react';
import {
  ResourceNeeds,
  WorkAssignment,
} from '../../../hooks/workAssignmentHooks';
import { ResourceCellDraft, ResourceCellKey } from './workAssignment.types';

type ResourceEditsMap = Record<string, Record<string, ResourceCellDraft>>;
type ResourceTotals = Record<
  string,
  { required: number; have: number; need: number }
>;

const numberOrEmpty = (n: number | undefined) =>
  n === undefined || n === null ? '' : String(n);

const parseNum = (v: string | undefined) => {
  if (!v) return 0;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

const findResource = (
  wa: WorkAssignment | undefined,
  name: string
): ResourceNeeds | undefined =>
  wa?.resource_list?.find((r) => r.resourceName === name);

export const useResourceState = (
  workAssignments: WorkAssignment[] | undefined
) => {
  const [addedResources, setAddedResources] = useState<string[]>([]);
  const [deletedResources, setDeletedResources] = useState<Set<string>>(
    new Set()
  );
  const [resourceEdits, setResourceEdits] = useState<ResourceEditsMap>({});

  const existingResources = useMemo(() => {
    const set = new Set<string>();
    workAssignments?.forEach((w) =>
      w.resource_list?.forEach((r) => set.add(r.resourceName))
    );
    return Array.from(set);
  }, [workAssignments]);

  const resourceColumns = useMemo(
    () => [
      ...existingResources.filter((r) => !deletedResources.has(r)),
      ...addedResources.filter((r) => !deletedResources.has(r)),
    ],
    [existingResources, addedResources, deletedResources]
  );

  const computeNeed = (required: string, have: string): string => {
    if (required === '' && have === '') return '';
    return String(Math.max(0, parseNum(required) - parseNum(have)));
  };

  const getRowValues = (
    wa: WorkAssignment
  ): Record<string, ResourceCellDraft> => {
    const out: Record<string, ResourceCellDraft> = {};
    const edits = resourceEdits[wa.work_assignment_id] ?? {};
    for (const name of resourceColumns) {
      const orig = findResource(wa, name);
      const edit = edits[name] ?? {};
      const required = edit.required ?? numberOrEmpty(orig?.required);
      const have = edit.have ?? numberOrEmpty(orig?.have);
      out[name] = {
        required,
        have,
        need: computeNeed(required, have),
      };
    }
    return out;
  };

  const setResourceValue = (
    waId: string,
    name: string,
    key: ResourceCellKey,
    val: string
  ) => {
    setResourceEdits((prev) => ({
      ...prev,
      [waId]: {
        ...(prev[waId] ?? {}),
        [name]: { ...((prev[waId] ?? {})[name] ?? {}), [key]: val },
      },
    }));
  };

  const addResource = (name: string) => {
    if (!name) return;
    if (resourceColumns.includes(name)) {
      setDeletedResources((prev) => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
      return;
    }
    setAddedResources((prev) => [...prev, name]);
  };

  const deleteResource = (name: string) => {
    setDeletedResources((prev) => new Set(prev).add(name));
    setAddedResources((prev) => prev.filter((r) => r !== name));
  };

  const reset = () => {
    setAddedResources([]);
    setDeletedResources(new Set());
    setResourceEdits({});
  };

  const buildResourceList = (wa: WorkAssignment): ResourceNeeds[] => {
    const values = getRowValues(wa);
    return resourceColumns.map((name) => ({
      resourceName: name,
      required: parseNum(values[name]?.required),
      have: parseNum(values[name]?.have),
      need: parseNum(values[name]?.need),
    }));
  };

  const hasStructuralChanges =
    addedResources.length > 0 || deletedResources.size > 0;

  const hasEdits = (waId: string) => !!resourceEdits[waId];

  const computeTotals = (rows: WorkAssignment[]): ResourceTotals => {
    const totals: ResourceTotals = {};
    for (const name of resourceColumns) {
      totals[name] = { required: 0, have: 0, need: 0 };
    }
    for (const wa of rows) {
      const values = getRowValues(wa);
      for (const name of resourceColumns) {
        totals[name].required += parseNum(values[name]?.required);
        totals[name].have += parseNum(values[name]?.have);
        totals[name].need += parseNum(values[name]?.need);
      }
    }
    return totals;
  };

  return {
    resourceColumns,
    addResource,
    deleteResource,
    getRowValues,
    setResourceValue,
    buildResourceList,
    computeTotals,
    hasStructuralChanges,
    hasEdits,
    reset,
  };
};
